import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Copy, Zap, Code, FileText, PlayCircle, ArrowRight, Star, Download, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getFlowIcon } from '@/lib/flowIconMapping.tsx';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { CommentSection } from '@/components/common/CommentSection';
import { flowService } from '@/lib/api/services';

// Helper function to fetch a flow by its ID from the API
const fetchFlowById = async (id: string) => {
  try {
    return await flowService.getById(id);
  } catch (error) {
    console.error('Error fetching flow:', error)
    return null
  }
};

// Mock data for related flows
const mockRelatedFlows = [
  {
    id: 'related-1',
    name: 'Advanced Image Classifier',
    description: 'High-accuracy image classification with custom model support and batch processing.',
    author: 'AI Vision Labs',
    rating: 4.8,
    downloads: 15420,
    image: '/placeholder.svg',
    category: 'Computer Vision',
    credits: 3
  },
  {
    id: 'related-2',
    name: 'Smart Text Summarizer',
    description: 'Intelligent text summarization with customizable length and focus areas.',
    author: 'NLP Solutions',
    rating: 4.6,
    downloads: 8790,
    image: '/placeholder.svg',
    category: 'Natural Language Processing',
    credits: 2
  },
  {
    id: 'related-3',
    name: 'Audio Transcription Pro',
    description: 'Professional audio transcription with speaker identification and timestamps.',
    author: 'AudioTech Inc',
    rating: 4.7,
    downloads: 12380,
    image: '/placeholder.svg',
    category: 'Audio Processing',
    credits: 4
  }
];

export default function FlowDetailPage() {
  const { flowId } = useParams();
  const location = useLocation();
  const [flow, setFlow] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Fetch flow data on component mount
  useEffect(() => {
    const loadFlow = async () => {
      if (!flowId) return;
      setLoading(true);
      const flowData = await fetchFlowById(flowId);
      setFlow(flowData);
      setLoading(false);
    };
    loadFlow();
  }, [flowId]);

  // Handle hash navigation to comments section
  useEffect(() => {
    if (location.hash === '#comments') {
      const commentsSection = document.getElementById('comments-section');
      if (commentsSection) {
        commentsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <Header currentPage="flows" variant="gallery" />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Loading...</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">Loading flow details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <Header currentPage="flows" variant="gallery" />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Flow not found</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">The flow you're looking for doesn't exist.</p>
          <Link to="/gallery/flows">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Flow Store
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const IconComponent = getFlowIcon(flow.icon as string);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <Header currentPage="flows" variant="gallery" />

      {/* Hero Section */}
      <div className="bg-white dark:bg-slate-800 border-b">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <Link to="/gallery/flows">
              <Button variant="ghost" className="mb-6 text-green-600 hover:text-green-800">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Flow Store
              </Button>
            </Link>

            <div className="flex flex-col md:flex-row items-start md:justify-between mb-8">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center">
                  <IconComponent className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-3">{flow.name}</h1>
                  <p className="text-xl text-slate-600 dark:text-slate-300 mb-4 max-w-2xl">{flow.description}</p>

                  <div className="flex items-center gap-3 mb-4">
                    <Badge
                      variant={
                        flow.difficulty === 'Beginner' ? 'secondary' : flow.difficulty === 'Intermediate' ? 'default' : 'destructive'
                      }>
                      {flow.difficulty}
                    </Badge>
                    <Badge variant="outline">{flow.category}</Badge>
                    {flow.featured && (
                      <Badge className="bg-green-600">
                        <Zap className="h-3 w-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    <div className="flex items-center space-x-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">{flow.rating}</span>
                    </div>
                  </div>

                  {/* Author info */}
                  <div className="flex items-center space-x-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={flow.author?.avatar || '/placeholder.svg'} alt={flow.author?.name || 'Unknown'} />
                      <AvatarFallback className="text-xs">
                        {flow.author?.name
                          ?.split(' ')
                          .map((n: string) => n[0])
                          .join('') || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-slate-600 dark:text-slate-400">by {flow.author?.name || 'Unknown Author'}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6 md:mt-0">
                <div className="flex items-center space-x-1 bg-green-50 dark:bg-green-900 px-3 py-2 rounded-lg">
                  <Zap className="h-4 w-4 text-green-600" />
                  <span className="font-semibold text-green-600 text-lg">{flow.creditsPerRun || flow.credits || 0} credits</span>
                </div>
                <Button className="bg-green-600 hover:bg-green-700">
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Add to Workflow
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Flow Preview Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <PlayCircle className="h-8 w-8 mr-3 text-green-600" />
              Flow Preview
            </h2>

            <Card className="bg-white dark:bg-slate-800">
              <CardContent className="p-8">
                <div className="w-full h-80 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                  <img src={flow.image || '/placeholder.svg'} alt={`${flow.name} preview`} className="w-full h-full object-cover" />
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* Inputs & Outputs Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <Code className="h-8 w-8 mr-3 text-green-600" />
              Inputs & Outputs
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Inputs */}
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Inputs</CardTitle>
                  <CardDescription>Data this flow accepts</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {flow.inputs ? Object.entries(flow.inputs).map(([name, type], index) => (
                    <div key={index} className="border-b border-slate-200 dark:border-slate-700 last:border-b-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <code className="bg-muted px-2 py-1 rounded text-sm font-mono">{name}</code>
                        <Badge variant="secondary">{type as string}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Input parameter of type {type}</p>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <Code className="w-8 h-8 mx-auto mb-2" />
                      <p>Input specifications will be loaded dynamically</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Outputs */}
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Outputs</CardTitle>
                  <CardDescription>Data this flow produces</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {flow.outputs ? Object.entries(flow.outputs).map(([name, type], index) => (
                    <div key={index} className="border-b border-slate-200 dark:border-slate-700 last:border-b-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <code className="bg-muted px-2 py-1 rounded text-sm font-mono">{name}</code>
                        <Badge variant="secondary">{type as string}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Output parameter of type {type}</p>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <Code className="w-8 h-8 mx-auto mb-2" />
                      <p>Output specifications will be loaded dynamically</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator />

          {/* Stats & Details Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <FileText className="h-8 w-8 mr-3 text-green-600" />
              Details & Statistics
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Flow Stats */}
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Flow Statistics</CardTitle>
                  <CardDescription>Usage and performance metrics</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <Download className="w-6 h-6 mx-auto mb-2 text-green-600" />
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{(flow.downloads || flow.uses || 0).toLocaleString()}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Downloads</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <MessageCircle className="w-6 h-6 mx-auto mb-2 text-green-600" />
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{flow.comments?.length || 0}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Comments</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Category</span>
                      <span className="text-sm text-slate-600 dark:text-slate-400">{flow.category}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Difficulty</span>
                      <Badge
                        variant={
                          flow.difficulty === 'Beginner' ? 'secondary' : flow.difficulty === 'Intermediate' ? 'default' : 'destructive'
                        }>
                        {flow.difficulty}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Rating</span>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">{flow.rating}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Version</span>
                      <span className="text-sm text-slate-600 dark:text-slate-400">{flow.version || '1.0.0'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tags & Actions */}
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Tags & Actions</CardTitle>
                  <CardDescription>Categories and quick actions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-3">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {flow.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-sm">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Separator />
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-900 dark:text-white">Quick Actions</h4>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Flow URL
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        <Download className="h-4 w-4 mr-2" />
                        Download Flow
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Contact Author
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator />

          {/* Documentation Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <FileText className="h-8 w-8 mr-3 text-green-600" />
              Documentation
            </h2>

            <Card className="bg-white dark:bg-slate-800">
              <CardContent className="p-8">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  {flow.documentation ? (
                    <pre className="whitespace-pre-wrap text-sm">{flow.documentation}</pre>
                  ) : (
                    <>
                      <h3>Overview</h3>
                      <p>
                        The {flow.name} flow provides {flow.description.toLowerCase()}. This AI-powered component can be easily integrated
                        into your workflows to enhance processing capabilities and deliver consistent results.
                      </p>

                      <h3>Key Features</h3>
                      <ul>
                        <li>
                          <strong>Easy Integration:</strong> Drop-in component that works with existing workflows
                        </li>
                        <li>
                          <strong>Scalable Processing:</strong> Handles both single items and batch operations
                        </li>
                        <li>
                          <strong>Reliable Performance:</strong> Battle-tested with {(flow.downloads || flow.uses || 0).toLocaleString()} downloads
                        </li>
                        <li>
                          <strong>Cost Effective:</strong> Only {flow.creditsPerRun || flow.credits || 0} credits per execution
                        </li>
                      </ul>

                      <h3>Usage Guidelines</h3>
                      <p>
                        This flow is categorized as <strong>{flow.difficulty}</strong> level, making it
                        {flow.difficulty === 'Beginner'
                          ? 'perfect for users just getting started with AI workflows'
                          : flow.difficulty === 'Intermediate'
                            ? 'suitable for users with some workflow experience'
                            : 'designed for advanced users with complex requirements'}
                        .
                      </p>

                      <h3>Community Feedback</h3>
                      <p>
                        With a {flow.rating}/5.0 rating from {flow.reviews ?? 0} reviews, this flow has proven to be reliable and effective
                        for production use cases. The community has highlighted its ease of use and consistent performance across different
                        input types.
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* Comments Section */}
          <CommentSection
            itemId={flow.id.toString()}
            itemType="flow"
            accentColor="green"
            comments={[
              {
                id: 1,
                user: 'Alex Johnson',
                avatar: '/placeholder.svg',
                rating: 5,
                comment: 'This flow is incredibly efficient! It processes images exactly as described and the quality is outstanding.',
                timestamp: '3 days ago',
                likes: 15
              },
              {
                id: 2,
                user: 'Maria Santos',
                avatar: '/placeholder.svg',
                rating: 4,
                comment:
                  'Great flow for batch processing. Saved me tons of time on my project. Setup was straightforward and it handles edge cases well.',
                timestamp: '1 week ago',
                likes: 9
              }
            ]}
            onAddComment={(comment) => {
              console.log('New flow comment:', comment);
              // In real app, this would call an API
            }}
          />

          <Separator />

          {/* Related Flows Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <ArrowRight className="h-8 w-8 mr-3 text-green-600" />
              Related Flows
            </h2>

            <div className="grid md:grid-cols-1 gap-6 mb-8">
              {mockRelatedFlows.map((relatedFlow) => (
                <Card key={relatedFlow.id} className="bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      <img
                        src={relatedFlow.image}
                        alt={`${relatedFlow.name} preview`}
                        className="w-24 h-16 object-cover rounded-lg bg-slate-100 dark:bg-slate-700"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                          <Link to={`/flow/${relatedFlow.id}`} className="hover:text-green-600 hover:underline">
                            {relatedFlow.name}
                          </Link>
                        </h3>
                        <p className="text-slate-600 dark:text-slate-300 mb-3">{relatedFlow.description}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                          <span>by {relatedFlow.author}</span>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span>{relatedFlow.rating}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Download className="h-4 w-4" />
                            <span>{relatedFlow.downloads} downloads</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Zap className="h-4 w-4 text-green-600" />
                            <span>{relatedFlow.credits} credits</span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        View Flow
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Link to="/gallery/flows">
                <Button variant="outline" size="lg" className="bg-green-600 text-white hover:bg-green-700">
                  <PlayCircle className="h-5 w-5 mr-2" />
                  Browse All Flows
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
